import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from "@ngrx/store";
import { AppLocalStorage } from 'app/app-storage-manager/local-storage';
import { AppState } from "app/reducers";
import { MiscService } from 'app/shared/misc.service';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';
import { PermissionService } from 'app/shared/permissions/permission.service';
import { AuthService } from '../_services/auth.service';
import * as authActions from './auth.actions';

@Injectable({
  providedIn: 'root'
})
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      exhaustMap((action) => {
        const credentials = action.credentials;
        return this.authService.login(credentials.email, credentials.password).pipe(
          map((res: any) => {
            if (res?.success === false || !res?.data?.sessionId) {
              this.toastr.error(res?.message || 'Failed to send verification code. Please try again.');
              return authActions.loginFailed({ message: res?.message || 'Failed to send verification code', code: 0 });
            }
            sessionStorage.setItem('mfa_session_id', res.data.sessionId);
            // Store only the email (used later for user.email); never the password.
            // Resend uses /resend-otp with just the sessionId, so the password isn't needed.
            sessionStorage.setItem('mfa_username', credentials.email);
            this.router.navigate(['/auth/mfa']);
            return authActions.mfaPending();
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error?.error?.message || error?.error?.errors || 'Network error');
            return of(authActions.loginFailed({ message: error?.error?.message || error?.error?.errors, code: error.status }));
          })
        );
      })
    )
  );

  verifyMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.verifyMfa),
      exhaustMap(({ sessionId, otpCode }) =>
        this.authService.verifyMfa(sessionId, otpCode).pipe(
          map((response: any) => {
            const token = response?.data?.token;
            if (response?.success === false || !token) {
              throw new Error(response?.message || 'Invalid OTP. Please try again.');
            }
            // Secondary merchant-scoped token returned alongside the primary token.
            // Stored for the specific downstream call that requires it (wired later).
            const xcelToken = response?.data?.xcel_token;
            if (xcelToken) {
              localStorage.setItem('xcel_token', xcelToken);
            }
            // Capture the login email before clearing the MFA session; it is the
            // logged-in user's own address, used to prefill account-statement delivery.
            const loginEmail = sessionStorage.getItem('mfa_username') || '';
            this.completeMfaLogin(token, loginEmail);
            this.clearMfaSession();
            return authActions.verifyMfaSuccess();
          }),
          catchError((error: any) => {
            const msg = error?.error?.errors || error?.error?.message || error?.message || 'Invalid OTP. Please try again.';
            this.toastr.error(msg);
            return of(authActions.verifyMfaFailed());
          })
        )
      )
    )
  );

  private clearMfaSession(): void {
    sessionStorage.removeItem('mfa_session_id');
    sessionStorage.removeItem('mfa_username');
    sessionStorage.removeItem('mfa_resend_at');
  }

  private completeMfaLogin(token: string, loginEmail: string): void {
    // Store the token first so PermissionService derives roles from it.
    localStorage.setItem('xs_access_token', token);
    const payload = this.permissionService.decodeJwt(token) || {};
    const merchantId = payload.merchant_id;
    // A sub-user token (e.g. a checker) carries a user_id + roles; a merchant-owner
    // token carries only merchant_id and therefore has unrestricted access.
    const isSubUser = !!payload.user_id;
    const userType = isSubUser ? 'user' : 'merchant';

    // VAPT #2/#3 hardening: roles / permissions / user_type are NOT persisted to
    // localStorage. They are derived in-memory from the signed JWT via
    // PermissionService, so forging those keys has no effect (nothing reads them).
    // Only the token is stored. Clear any legacy keys from older sessions.
    localStorage.removeItem('user-permissions');
    this.storageManager.removeFromStorage('feature_permissions');
    this.storageManager.removeFromStorage('user_type');

    // The JWT carries identity + permissions but not the merchant record (currency,
    // country_code, etc.). Fetch it via sub-merchants using the token's merchant_id.
    this.miscService.fetchSubMerchants(merchantId).subscribe({
      next: (response: any) => {
        const merchants = response?.data || [];
        const merchant = merchants.find((m: any) => m.merchant_id === merchantId) || merchants[0];
        // The merchant record must carry currency/country_code — many downstream
        // screens (account statement, banks, balance inquiry) read them without
        // null-checks. If it's missing, fail login rather than log in a broken session.
        if (!merchant || !merchant.currency) {
          this.failMfaLogin();
          return;
        }
        this.setSessionUser({ ...merchant, merchant_id: merchantId, user_id: payload.user_id, email: loginEmail, type: userType });
      },
      error: () => {
        this.failMfaLogin();
      }
    });
  }

  private failMfaLogin(): void {
    // A partial session (token set, but no merchant record) would crash currency-
    // dependent screens. Clear everything and send the user back to sign in.
    localStorage.clear();
    sessionStorage.clear();
    this.toastr.error('We could not load your account details. Please sign in again.');
    this.router.navigate(['/auth']);
  }

  private setSessionUser(user: any): void {
    this.storageManager.storeToStorage('user', user);
    this.miscService.updateUser(user);
    this.router.navigate(['/app/dashboard']);
  }

  fetchEntityTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchEntityTypes),
      exhaustMap((action) =>
        this.authService.fetchEntityTypes(action.countryCode).pipe(
          exhaustMap((response: any) => [authActions.fetchEntityTypesSuccess({ entityTypes: response })]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.fetchEntityTypesFailed());
          })
        )
      )
    )
  );

  fetchBusinessTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchBusinessTypes),
      exhaustMap((action) =>
        this.authService.fetchBusinessTypes().pipe(
          exhaustMap((response: any) => [authActions.fetchBusinessTypesSuccess({ businessTypes: response.data })]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.fetchBusinessTypesFailed());
          })
        )
      )
    )
  );
  fetchTransactionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchTransactionTypes),
      exhaustMap((action) =>
        this.authService.fetchTransactionTypes().pipe(
          exhaustMap((response: any) => [
            authActions.fetchTransactionTypesSuccess({ transactionTypes: response.data })
          ]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.fetchTransactionTypesFailed());
          })
        )
      )
    )
  );
  createMerchant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.createMerchant),
      exhaustMap((action) =>
        this.authService.createMerchant(action.requestPayload).pipe(
          exhaustMap((response: any) => [authActions.createMerchantSuccess(response)]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(authActions.createMerchantFailed());
          })
        )
      )
    )
  );

  requestPasswordReset = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.requestPasswordReset),
      exhaustMap((action) =>
        this.authService
          .requestPasswordRequest(action.payload)
          .pipe(
            tap((response: any) => {
              this.toastr.success(response.message);
              sessionStorage.setItem("resetPassId", action.payload.email)
            }),
            exhaustMap((response: any) => [
              authActions.requestPasswordResetSuccess(response),
            ]),
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(authActions.requestPasswordResetFailed())
            }
            )
          )
      )
    )
  )

  resetPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.resetPassword),
      exhaustMap((action) =>
        this.authService
          .resetPassword(action.payload)
          .pipe(
            tap((response: any) => {
              this.toastr.success(response.message);
              sessionStorage.removeItem("resetPassId")
              this.router.navigateByUrl("/auth")
            }),
            exhaustMap((response: any) => [
              authActions.resetPasswordSuccess(response),
            ]),
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(authActions.resetPasswordFailed())
            }
            )
          )
      )
    )
  )

  fetchCompanyProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchCompanyProfile),
      exhaustMap((action) =>
        this.authService.getCompanyProfile(action.companyNumber).pipe(
          exhaustMap((response: any) => [authActions.fetchCompanyProfileSuccess({ companyProfile: response.data })]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.info(error.error?.message, 'Could not validate incorporation number on company house');
            return of(authActions.fetchCompanyProfileFailed());
          })
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      tap(() => {
        localStorage.clear();
        this.router.navigate(['/auth']);
      }),
      exhaustMap(() => [])
    )
  );
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private storageManager: AppLocalStorage,
    private toastr: ToastrService,
    private miscService: MiscService,
    private router: Router,
    private store: Store<AppState>,
    private permissionService: PermissionService,
  ) { }
}
